import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import api from "api/api";

// mui components
import CircularProgress from "@mui/material/CircularProgress";

//import components
import ProductInfo from "../../common/ProductInfo";
import ConsumerFilter from "../../common/ConsumerFilter";
import SkewTabs from "../../common/analysisModule/SkewTabs";
import ColumnVisibility from "components/common/ColumnVisibility";
import DragRenderTable from "../../common/analysisModule/DragRenderTable";
import HeaderSampleSize from "../../common/analysisModule/HeaderSampleSize";
import AnalysisTableBottomLabel from "../../common/analysisModule/AnalysisTableBottomLabel";
import RemoveRestoreDialog from "../../common/analysisModule/removeRestore/RemoveRestoreDialog";
import TableCellWithInnerSeparator from "../../common/analysisModule/TableCellWithInnerSeparator";
import TableCellWithOuterSeparator from "../../common/analysisModule/TableCellWithOuterSeparator";
import RemoveRestoreBvCheckbox from "../../common/analysisModule/removeRestore/RemoveRestoreBvCheckbox";

// utils
import SortFinalData from "../../../utils/SortFinalData";
import GetMacroCategoryOrder from "../../../utils/GetMacroCategoryOrder";

// assets
import WeightToggleIcon from "../../../assets/icons/weightToggle.png";

// redux
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadProductOrderState } from "store/features/productsInfoSlice";

// utils
import { getRecallAndITM, getProductScoreWithNaAndRowSpan, getScoreByVariable, getProductColumnClassName, getViewKeys, highlightText, getFormattedScoreOrNA, gapRow, getDataView, getSortedDataMapping, getAlphabeticalMacroCategories } from "../../../utils/analysisTable/analysisUtils";
import { getTestResultByVariable, getColorsAndLetter } from "../../../utils/analysisTable/tTestutils";

function AnalysisTable({ productsSecondaryData = [], handleFilterSubmit = () => { }, analysisLevelData = {}, tTest = {}, settings = {} }) {
    const dispatch = useDispatch();
    const { isITMDataShown = false, tableConfig, moduleName, boxAnalysisTypeSelection = [], analysisTypeValue, mappings } = analysisLevelData;
    const { tTestLegends, isFisher, tTestResult, testLabel } = tTest;
    const { activeTab, setActiveTab } = settings;
    const { stickyColumns = [] } = tableConfig;
    const isConsumerFilterLoadingStoreData = useSelector((state) => state.consumerFilterStore.isLoading);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);

    const overallToggle = useSelector((state) => state.analysisResultsStore.overallToggle);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    // tTest
    const testAnalysisType = useSelector((state) => state.tTestStore.testAnalysisType);

    // dataview
    const overallAnalysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.overallAnalysisTypeValue);
    const fragranceMetricsAnalysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.fragranceMetricsAnalysisTypeValue);
    const preferenceAnalysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.preferenceAnalysisTypeValue);

    const hiddenProductsCount = Object.values(productColumnVisibility || {}).filter((datum) => datum === true)?.length || 0;

    const [productDetailsDialogueOpen, setProductDetailsDialogueOpen] = useState(false);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);

    // signifcance toggle
    const significanceToggle = useSelector((state) => state.analysisResultsStore.significanceToggle);

    // preference percentage toggle
    const percentageToggle = useSelector((state) => state.analysisResultsStore.percentageToggle);

    // column drag and drop variables
    const [draggableColumns, setDraggableColumns] = useState(productsSecondaryData);
    const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
    const [draggedColumnDiamension, setDraggedColumnDiamension] = useState({ width: null, height: null });
    const [targetColumnIndex, setTargetColumnIndex] = useState(null);

    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [hasActuallyMoved, setHasActuallyMoved] = useState(false);

    const tableRef = useRef(null);
    const tbodyRef = useRef(null);
    const draggedElementRef = useRef(null);
    const initialMousePos = useRef(null);

    const [actionStandardKpis, setActionStandardKpis] = useState([]);
    const [actionStandardData, setActionStandardData] = useState([]);

    const [showCheckBox, setShowCheckBox] = useState(false);
    const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);

    const [removedKpi, setRemovedKpi] = useState([]);
    const [disableRestore, setDisableRestore] = useState(true);

    let noOfProducts = productsSecondaryData.length;

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const customOrderMacroCategory = GetMacroCategoryOrder(productCategoryData?.label?.toLowerCase(), moduleName);

    const [dataViewKey, setDataViewKey] = useState(""); //ORDER [top:top_weight:mean:weight_mean, bottom:bottom_weight]
    const [tableDataMapping, setTableDataMapping] = useState([]);

    const [selectedProductInfo, setSelectedProductInfo] = useState({});

    useEffect(() => {
        setRemovedKpi([...removedKpiStoreState]);
        setDisableRestore(removedKpiStoreState.length === 0);
    }, [moduleName, removedKpiStoreState]);

    useEffect(() => {
        let macroCategoryOrder = customOrderMacroCategory;
        if (!macroCategoryOrder && moduleName !== "claims") {
            macroCategoryOrder = getAlphabeticalMacroCategories(mappings);
        }
        let sortedData = getSortedDataMapping(mappings, macroCategoryOrder, moduleName, isSkinCleansing || isOralCare);

        if (moduleName !== "claims") {
            if (activeTab) {
                sortedData = sortedData.filter((each) => each.macroCategory === activeTab);
            }
            // Only update if different to avoid infinite loop
            setTableDataMapping((prev) => {
                const isEqual = JSON.stringify(prev) === JSON.stringify(sortedData);
                return isEqual ? prev : sortedData;
            });
        } else {
            setTableDataMapping([...sortedData]);
        }
    }, [customOrderMacroCategory, mappings, activeTab, moduleName, isSkinCleansing, isOralCare]);

    useEffect(() => {
        const _dataViewKey = getDataView(analysisTypeValue, weightToggle, boxAnalysisTypeSelection, percentageToggle);
        setDataViewKey(_dataViewKey);
    }, [analysisTypeValue, boxAnalysisTypeSelection, weightToggle, percentageToggle]);

    useEffect(() => {
        setDraggableColumns(SortFinalData(productsSecondaryData, productsOrder));
    }, [productsSecondaryData, productsOrder]);

    const addBorders = useCallback(() => {
        // Check if the tbody element is available
        if (tbodyRef.current) {
            // Get all <tr> (table row) elements within the tbody
            const rows = Array.from(tbodyRef.current.querySelectorAll("tr.score-row"));
            let currentGroupLastRowIndex = 0;
            rows.forEach((row, index) => {
                const tds = row.querySelectorAll("td");
                const firstTd = row.querySelector("td");
                tds.forEach((td, i) => {
                    // clear all border styles
                    td.style.borderTop = "";
                    td.style.borderBottom = "";
                    td.style.borderRight = "";
                    td.style.borderLeft = "";
                    td.style.borderTopLeftRadius = "";
                    td.style.borderTopRightRadius = "";
                    td.style.borderBottomLeftRadius = "";
                    td.style.borderBottomRightRadius = "";
                    td.style.borderBottomWidth = "0px";

                    if (firstTd.hasAttribute("rowspan")) {
                        currentGroupLastRowIndex = index + firstTd.rowSpan - 1;
                        firstTd.style.borderTopLeftRadius = "8px"; // Round the top left corner
                        firstTd.style.borderBottomLeftRadius = "8px"; // Round the bottom left corner
                        firstTd.style.borderLeft = "2px solid #005eef80"; // Add border on the left
                        firstTd.style.borderBottom = "2px solid #005eef80";
                        td.style.borderTop = "2px solid #005eef80";
                        if (i === tds.length - 1) {
                            td.style.borderTopRightRadius = "8px"; // Round the top left corner
                            td.style.borderRight = "2px solid #005eef80";
                        }
                    } else if (i === tds.length - 1) {
                        td.style.borderRight = "2px solid #005eef80";
                    }

                    // identifying the last row and put bottom border
                    if (currentGroupLastRowIndex === index && i < tds.length - 1) {
                        td.style.borderBottom = "2px solid #005eef80";
                    } else if (currentGroupLastRowIndex === index && i === tds.length - 1) {
                        td.style.borderBottomRightRadius = "8px";
                        td.style.borderBottom = "2px solid #005eef80";
                    }
                });
            });
        }
    }, []);

    // useEffect hook to call addBorders after a delay upon certain dependencies changing
    useEffect(() => {
        if (moduleName === "overall" || moduleName === "fragranceMetrics" || moduleName === "preference") {
            // Set a timeout to delay the execution of addBorders function
            const addBr = setTimeout(() => {
                if (!hasActuallyMoved) {
                    addBorders(); // Call the addBorders function after the delay
                }
            }, 10); // Wait for 10 milliseconds

            // Cleanup function to clear the timeout if the component unmounts or dependencies change
            return () => clearTimeout(addBr);
        }
    }, [addBorders, mappings, draggableColumns, productColumnVisibility, overallToggle, removedKpi, hasActuallyMoved, overallAnalysisTypeValue, fragranceMetricsAnalysisTypeValue, preferenceAnalysisTypeValue, moduleName, isITMDataShown]); // Dependencies that trigger the effect

    const getKpisFromResponse = (response) => {
        const kpis = new Set(
            response
                .map((datum) => {
                    const _kpi = datum?.action_standard_kpis?.map((item) => item?.kpi || []);
                    return _kpi.flat();
                })
                .flat()
        );

        return [...kpis];
    };

    const getActionStandard = useCallback(() => {
        if (selectedStudy.length === 1) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            const kpis = getKpisFromResponse(response);
                            const kpisArray = kpis.map(item => item.backend_variable)
                            setActionStandardKpis([...kpisArray]);
                            setActionStandardData(response);
                        })
                        .catch(async (error) => {
                            handleError("error", error);
                        });
                } catch (error) {
                    handleError("error", error);
                }
            }
        }
    }, [selectedStudy, moduleName, isOralCare]);

    useEffect(() => {
        getActionStandard();
    }, [getActionStandard]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const returnTableHeader = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    return i === 0 ? (
                        <TableCellWithInnerSeparator
                            key={col + i}
                            value={col}
                            cellType="th"
                            testLabel=""
                            styles={{
                                main: {
                                    minWidth: moduleName === "claims" ? 300 : 200,
                                    maxWidth: moduleName === "claims" ? 300 : 200,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 2,
                                },
                                wrapper: {},
                                value: {
                                    alignItems: "center",
                                },
                            }}
                        />
                    ) : (
                        <TableCellWithOuterSeparator
                            key={col + i}
                            value={col}
                            cellType="th"
                            testLabel=""
                            styles={{
                                main: {
                                    minWidth: 300,
                                    maxWidth: moduleName === "claims" ? 500 : 300,
                                    position: "sticky",
                                    left: moduleName === "claims" ? 300 : 200,
                                    zIndex: 2,
                                },
                                wrapper: {
                                    justifyContent: "space-between",
                                },
                                value: {
                                    alignItems: "center",
                                },
                            }}
                        />
                    );
                })}
                {significanceToggle && (
                    <TableCellWithOuterSeparator
                        value="Significance (p value)"
                        cellType="th"
                        testLabel=""
                        mainProps={{
                            colSpan: 1,
                            align: "center",
                        }}
                        styles={{
                            main: {
                                minWidth: 200,
                                maxWidth: 200,
                                zIndex: 2,
                            },
                            wrapper: {},
                            value: {
                                justifyContent: "center",
                            },
                        }}
                    />
                )}
                {draggableColumns.map((datum, index) => {
                    let productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    let studyReferenceOrTestCode = selectedStudy?.find((study) => study.id === datum.coreDataId)?.study_reference__title;
                    const showWeightIcon = selectedStudy.length > 1 && selectedStudy?.find((study) => study.id === datum.coreDataId)?.weighted === true;
                    return (
                        <TableCellWithOuterSeparator
                            key={index + 1 + datum.title + "pc"}
                            value={
                                targetColumnIndex === index && hasActuallyMoved ? (
                                    ""
                                ) : (
                                    <>
                                        {showWeightIcon && (
                                            <img
                                                alt="weightToggle"
                                                style={{
                                                    width: 14,
                                                    marginRight: 10,
                                                }}
                                                src={WeightToggleIcon}
                                            />
                                        )}
                                        <button
                                            className="clean-button color-blue"
                                            style={{
                                                fontWeight: 600,
                                                fontSize: "0.875rem",
                                            }}
                                            onClick={(e) =>
                                                handleProductClick(e, {
                                                    coreDataId: datum.coreDataId,
                                                    productCode: datum.title,
                                                })
                                            }
                                        >
                                            {productCodeTitle} / {studyReferenceOrTestCode}
                                        </button>

                                        <HeaderSampleSize productData={datum} />
                                    </>
                                )
                            }
                            cellType="th"
                            testLabel=""
                            mainProps={{
                                colSpan: 1,
                                draggable: true,
                                onDragStart: (e) => handleColumnDragStart(e, index),
                                onDragOver: handleDragOver,
                                onDrop: () => handleColumnDrop(index),
                                align: "center",
                                className: className,
                            }}
                            styles={{
                                main: {
                                    fontWeight: "600",
                                    cursor: "move",
                                    minWidth: targetColumnIndex === index && hasActuallyMoved ? draggedColumnDiamension.width : 200,
                                    maxWidth: targetColumnIndex === index && hasActuallyMoved ? draggedColumnDiamension.height : 300,
                                    background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                    borderColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                },
                                wrapper: {
                                    justifyContent: "space-between",
                                },
                                value: {
                                    justifyContent: "space-between",
                                    flexDirection: "column",
                                    alignItems: "center",
                                },
                            }}
                        />
                    );
                })}
            </tr>
        );
    };

    const returnConsumerFilterRow = () => {
        return (
            <tr>
                {stickyColumns.map((col, i) => {
                    return (
                        <th
                            key={col + i + "sticky-col"}
                            scope="col"
                            style={{
                                minWidth: 200,
                                maxWidth: 200,
                                position: "sticky",
                                left: i * 200,
                                zIndex: 2,
                            }}
                        ></th>
                    );
                })}
                {significanceToggle && (
                    <td
                        colSpan={1}
                        style={{
                            minWidth: 200,
                            maxWidth: 200,
                            textAlign: "center",
                            fontWeight: "500",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "12px",
                                marginLeft: "30px",
                                marginRight: "30px",
                            }}
                        >
                            {`p-value comparison between ${tTestLegends[0]?.title} and ${tTestLegends[1]?.title}`}
                        </p>
                    </td>
                )}
                {draggableColumns.map((datum, index) => {
                    const productCodeTitle = datum.title;
                    const productCodeCoreDataId = datum.coreDataId;
                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                    return (
                        <td
                            key={index + 1 + "cf"}
                            colSpan={1}
                            className={className}
                            style={{
                                textAlign: "center",
                                fontWeight: "500",
                                padding: "6px 0px",
                                background: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "",
                                borderColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                            }}
                        >
                            {targetColumnIndex === index && hasActuallyMoved ? (
                                ""
                            ) : (
                                <>
                                    {!isConsumerFilterLoadingStoreData ? (
                                        <ConsumerFilter
                                            productsSecondaryData={draggableColumns}
                                            product={datum}
                                            handleFilterSubmit={handleFilterSubmit}
                                        />
                                    ) : (
                                        <CircularProgress size={20} />
                                    )}
                                </>
                            )}
                        </td>
                    );
                })}
            </tr>
        );
    };

    const getMacroCategoryLabel = (rowMacroCategoryLabel) => {
        const macroCategoryLabel = rowMacroCategoryLabel.split(",").map((tag, i) => {
            const key = i + 1 + tag;
            return (
                <div
                    key={key}
                    style={{
                        backgroundColor: "#D8E8FE",
                        borderRadius: "5px",
                        padding: "2px 6px",
                        display: "inline-block",
                        margin: "3px 6px",
                    }}
                >
                    {tag}
                </div>
            );
        });
        return macroCategoryLabel;
    };

    const getScoreCells = (recallBvObj, itmBvObj, kpis, rowDisplayLabel) => {
        const tds = draggableColumns.map((datum, index) => {
            let recallScore = null;
            let itmBvScore = null;
            let recallTest = null;
            let itmTest = null;
            const productCodeCoreDataId = datum.coreDataId;

            if (moduleName === "overall") {
                recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);
                recallTest = getTestResultByVariable(tTestResult, recallBvObj?.backendVariable, productCodeCoreDataId);

                if (isITMDataShown) {
                    itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
                    itmTest = getTestResultByVariable(tTestResult, itmBvObj?.backendVariable, productCodeCoreDataId);
                }
            } else {
                const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                recallTest = getTestResultByVariable(tTestResult, filteredBvObjScore?.backendVariable, productCodeCoreDataId);
            }

            const productCodeTitle = datum.title;
            const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
            const recallTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, recallTest);
            const itmTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, itmTest);

            return (
                <>
                    {isITMDataShown &&
                        dataViewKey.split(",").map((ele, k) => {
                            const key = "box-analysis-itm-value-column-" + index + "-" + k;

                            return (
                                <TableCellWithOuterSeparator
                                    key={key}
                                    value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(itmBvScore[dataViewKey.split(",")[k]], ele.split(",")[0])}
                                    cellType="td"
                                    testLabel={itmTestResult?.["label"] ?? null}
                                    mainProps={{
                                        className: className,
                                    }}
                                    styles={{
                                        main: {
                                            backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                            border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                            outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                        },
                                        wrapper: {},
                                        value: {
                                            justifyContent: "center",
                                            background: itmTestResult?.["color"]?.[ele] ?? "#fff",
                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                            borderRadius: "5px",
                                            padding: "5px",
                                        },
                                    }}
                                    showSeperator={!ele.startsWith("bottom")}
                                />
                            );
                        })}
                    {dataViewKey.split(",").map((ele, k) => {
                        const key = "box-analysis-recall-value-column-" + index + "-" + k;
                        return (
                            <TableCellWithOuterSeparator
                                key={key}
                                value={targetColumnIndex === index && hasActuallyMoved ? "" : getFormattedScoreOrNA(recallScore[dataViewKey.split(",")[k]], ele.split(",")[0])}
                                cellType="td"
                                testLabel={recallTestResult?.["label"] ?? null}
                                mainProps={{
                                    className: className,
                                }}
                                styles={{
                                    main: {
                                        backgroundColor: targetColumnIndex === index && hasActuallyMoved ? "#d8eeff" : "#fff",
                                        border: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                        outline: targetColumnIndex === index && hasActuallyMoved ? "none" : "",
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        background: recallTestResult?.["color"]?.[ele] ?? "#fff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele.startsWith("bottom") ? false : !isITMDataShown}
                            />
                        );
                    })}
                </>
            );
        });
        return tds;
    };

    const getCheckClaimsProductCode = (bvObj) => {
        return removedKpiStoreState.flatMap((item) => item.split(",")).includes(bvObj.backendVariable);
    };

    const returnAnalysisScoresRow = () => {
        const [key1, key2] = getViewKeys(dataViewKey);
        const _box = false;
        const _roww = tableDataMapping.map((catObj, i) => {
            const kpis = catObj?.displayLabels || [];
            const { rowSpan, productScoreWithNaObj } = getProductScoreWithNaAndRowSpan({ catObj, _box, key1, key2, isITMDataShown, moduleName, draggableColumns, noOfProducts, removedKpiStoreState });
            let ifAvailableFirstRow = true;
            const uniqueKpis = new Set();
            const _trs = kpis
                .map((bvObj) => {
                    const rowDisplayLabel = bvObj.displayLabel;
                    const rowBackendVariable = bvObj.backendVariable;

                    let firstColumn = true;
                    if (!uniqueKpis.has(bvObj.displayLabel)) {
                        if (!ifAvailableFirstRow && catObj.displayLabels?.length >= 2) {
                            firstColumn = false;
                        }
                        const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);

                        const productsScoreWithNA = productScoreWithNaObj[rowDisplayLabel];
                        const rowMacroCategoryLabel = catObj.macroCategory.charAt(0).toUpperCase() + catObj.macroCategory.slice(1);
                        let rowBackendVariableDisplayLabel = bvObj.displayLabel.charAt(0).toUpperCase() + bvObj.displayLabel.slice(1);
                        if (likertScaleData?.[bvObj.backendVariable] && moduleName !== "claims" && moduleName !== "preference") {
                            rowBackendVariableDisplayLabel += ` (${likertScaleData[bvObj.backendVariable]})`;
                        }
                        const showActionStandard = actionStandardKpis.includes(bvObj.backendVariable) && selectedStudy.length === 1;
                        if (showActionStandard) {
                            rowBackendVariableDisplayLabel += "*";
                        }
                        const label = highlightText(rowBackendVariableDisplayLabel, "");
                        const testResult = tTestResult?.find((resObj) => resObj.backendVariable === rowBackendVariable);
                        const pValue = moduleName === "fragranceMetrics" || moduleName === "preference" ? testResult?.significance_pvalue : testResult?.significance;

                        const row = (
                            <tr
                                key={i + 1 + "row" + rowBackendVariable}
                                className="score-row"
                            >
                                {firstColumn && (moduleName === "overall" || moduleName === "claims" || moduleName === "fragranceMetrics" || moduleName === "preference") && (
                                    <TableCellWithInnerSeparator
                                        key={catObj.macroCategory + "first-co" + i}
                                        value={
                                            moduleName === "claims" ? (
                                                <div
                                                    style={{
                                                        wordWrap: "break-word",
                                                        whiteSpace: "normal",
                                                    }}
                                                >
                                                    {getMacroCategoryLabel(rowMacroCategoryLabel)}
                                                </div>
                                            ) : (
                                                rowMacroCategoryLabel
                                            )
                                        }
                                        cellType="td"
                                        testLabel=""
                                        mainProps={{
                                            rowSpan: rowSpan,
                                        }}
                                        styles={{
                                            main: {
                                                minWidth: 200,
                                                maxWidth: moduleName === "claims" ? 300 : 200,
                                                left: 0,
                                                position: "sticky",
                                                zIndex: 2,
                                            },
                                            wrapper: {
                                                background: "#fff",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                            value: {
                                                alignItems: "center",
                                            },
                                        }}
                                    />
                                )}
                                {moduleName === "overall" || moduleName === "claims" || moduleName === "fragranceMetrics" || moduleName === "preference" ? (
                                    <TableCellWithOuterSeparator
                                        value={
                                            <>
                                                {showCheckBox && (
                                                    <RemoveRestoreBvCheckbox
                                                        backendVariableSettings={{
                                                            removedKpi: removedKpi,
                                                            backendVariable: bvObj.backendVariable,
                                                            setRemovedKpi: setRemovedKpi,
                                                            mappings: tableDataMapping,
                                                        }}
                                                    />
                                                )}
                                                <span style={{ textAlign: "left" }}>
                                                    <span dangerouslySetInnerHTML={{ __html: label }} />
                                                </span>
                                            </>
                                        }
                                        cellType="td"
                                        testLabel=""
                                        styles={{
                                            main: {
                                                left: moduleName === "claims" ? 300 : 200,
                                                position: "sticky",
                                                fontWeight: showActionStandard ? 600 : 500, // Bold only for single study
                                                zIndex: 2,
                                            },
                                            wrapper: {},
                                            value: {
                                                background: "#fff",
                                                whiteSpace: "nowrap",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                    />
                                ) : (
                                    <TableCellWithInnerSeparator
                                        value={
                                            <>
                                                {showCheckBox && (
                                                    <RemoveRestoreBvCheckbox
                                                        backendVariableSettings={{
                                                            removedKpi: removedKpi,
                                                            backendVariable: bvObj.backendVariable,
                                                            setRemovedKpi: setRemovedKpi,
                                                            mappings: tableDataMapping,
                                                        }}
                                                    />
                                                )}
                                                <span style={{ textAlign: "left" }}>
                                                    <span dangerouslySetInnerHTML={{ __html: label }} />
                                                </span>
                                            </>
                                        }
                                        cellType="td"
                                        testLabel=""
                                        styles={{
                                            main: {
                                                left: 0,
                                                position: "sticky",
                                                fontWeight: showActionStandard ? 600 : 500, // Bold only for single study
                                                zIndex: 2,
                                            },
                                            wrapper: {},
                                            value: {
                                                background: "#fff",
                                                whiteSpace: "nowrap",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                    />
                                )}
                                {significanceToggle && (
                                    <TableCellWithOuterSeparator
                                        value={pValue}
                                        cellType="td"
                                        testLabel=""
                                        styles={{
                                            main: {},
                                            wrapper: {},
                                            value: {
                                                justifyContent: "center",
                                                background: "#fff",
                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                borderRadius: "5px",
                                                padding: "5px",
                                            },
                                        }}
                                    />
                                )}
                                {getScoreCells(recallBvObj, itmBvObj, kpis, rowDisplayLabel)}
                            </tr>
                        );

                        if (productsScoreWithNA === noOfProducts) {
                            return null;
                        } else if (moduleName !== "claims" && getCheckClaimsProductCode(bvObj)) {
                            return;
                        } else if (moduleName === "claims" && removedKpiStoreState.includes(bvObj.backendVariable)) {
                            return;
                        } else {
                            ifAvailableFirstRow = false;
                            uniqueKpis.add(bvObj.displayLabel);
                            return row;
                        }
                    } else {
                        return;
                    }
                })
                .filter((ele) => ele);
            let _tr = null;
            if ((moduleName === "overall" || moduleName === "fragranceMetrics" || moduleName === "preference") && _trs.length > 0) {
                _tr = (
                    <>
                        {_trs}
                        <tr className="gap">{gapRow({ length: draggableColumns.length, hiddenProductsCount, _box, boxAnalysisTypeSelection, isITMDataShown, overallToggle, dataViewKey, targetColumnIndex, hasActuallyMoved })}</tr>
                    </>
                );
            } else {
                _tr = _trs;
            }

            return _tr;
        });
        return _roww;
    };

    // column drag and drop logic

    const handleColumnDragStart = (e, index) => {
        e.dataTransfer.setData("columnIndex", index);
        // Create an invisible image to replace the default drag image
        const img = new Image();
        img.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP88xIAAX8B5sR8D6cAAAAASUVORK5CYII=";
        e.dataTransfer.setDragImage(img, 0, 0);
        setDraggedColumnIndex(index);
        const headerRect = e.target.getBoundingClientRect();
        setMousePosition({
            x: e.clientX - dragOffset.x,
            y: e.clientY - dragOffset.y,
        });

        setDragOffset({
            x: e.clientX - headerRect.left,
            y: e.clientY - headerRect.top,
        });

        setDraggedColumnDiamension({
            width: headerRect.width,
            height: headerRect.height,
        });
        setIsDragging(true);
        initialMousePos.current = { x: e.clientX, y: e.clientY };
        setHasActuallyMoved(false);
    };

    const handleColumnDrop = (index) => {
        const updatedColumns = [...draggableColumns];
        const [draggedColumn] = updatedColumns.splice(draggedColumnIndex, 1);
        updatedColumns.splice(index, 0, draggedColumn);

        setDraggableColumns(updatedColumns);
        setDraggedColumnIndex(null);
        setIsDragging(false);

        const _updateArray = updatedColumns.map((ele) => {
            return {
                productCode: ele.title,
                coreDataId: ele.coreDataId,
                isCompetitor: ele.isCompetitorProduct,
            };
        });
        dispatch(loadProductOrderState([..._updateArray]));
        setHasActuallyMoved(false);
        setTargetColumnIndex(null);
        initialMousePos.current = null;
        setDraggedColumnDiamension({
            width: null,
            height: null,
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isDragging) return;

        if (!hasActuallyMoved && initialMousePos.current) {
            const dx = Math.abs(e.clientX - initialMousePos.current.x);
            const dy = Math.abs(e.clientY - initialMousePos.current.y);
            if (dx > 5 || dy > 5) {
                setHasActuallyMoved(true);
            }
        }

        setMousePosition({
            x: e.clientX - dragOffset.x,
            y: e.clientY - dragOffset.y,
        });

        if (!hasActuallyMoved) return;

        const headers = Array.from(tableRef.current.querySelectorAll("th")).filter((header) => header.className.includes("checked--"));
        const draggedRect = draggedElementRef.current?.getBoundingClientRect();

        if (!draggedRect) return;

        const closestHeader = headers.reduce((closest, header, index) => {
            if (index === draggedColumnIndex) return closest;

            const rect = header.getBoundingClientRect();
            const center = rect.left + rect.width / 2;
            const distance = Math.abs(e.clientX - center);

            if (!closest || distance < closest.distance) {
                return { index, distance };
            }
            return closest;
        }, null);
        if (closestHeader) {
            if (draggedRect.left + draggedRect.width / 2 < headers[closestHeader.index].getBoundingClientRect().left + headers[closestHeader.index].getBoundingClientRect().width / 2) {
                if (hasActuallyMoved) {
                    setTargetColumnIndex(closestHeader.index);
                }
            }
        }
    };

    const clearAllDragVars = () => {
        setDraggedColumnIndex(null);
        setIsDragging(false);
        setHasActuallyMoved(false);
        setTargetColumnIndex(null);
        initialMousePos.current = null;
        setDraggedColumnDiamension({
            width: null,
            height: null,
        });
    };

    const handleProductClick = (e, info) => {
        setProductDetailsDialogueOpen(true);
        setSelectedProductInfo(info);
    };

    return (
        <div>
            <ColumnVisibility
                productsSecondaryData={draggableColumns}
                pageName={moduleName}
            />
            <div
                className="table-responsive"
                style={{
                    width: "100%",
                    position: "relative",
                    minHeight: "584px",
                    top: -25,
                }}
            >
                <table
                    ref={tableRef}
                    onMouseLeave={clearAllDragVars}
                    className="table text-sm analysis-table"
                    style={{
                        background: "transparent",
                        borderCollapse: "separate",
                        borderSpacing: "0",
                    }}
                >
                    <thead>{returnTableHeader()}</thead>
                    <tbody ref={tbodyRef}>
                        {returnConsumerFilterRow()}
                        {returnAnalysisScoresRow()}
                    </tbody>
                </table>
            </div>
            {isDragging && draggedColumnIndex !== null && hasActuallyMoved && (
                <DragRenderTable
                    dragSettings={{
                        draggableColumns: draggableColumns,
                        draggedColumnIndex: draggedColumnIndex,
                        mousePosition: mousePosition,
                        draggedColumnDiamension: draggedColumnDiamension,
                        tableHeadSpan: 1,
                        draggedElementRef: draggedElementRef,
                    }}
                    analysisView={{
                        box: false,
                        isITMDataShown: isITMDataShown,
                        moduleName: moduleName,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                    }}
                    productData={{
                        noOfProducts: noOfProducts,
                    }}
                    tableData={{
                        tableDataMapping: tableDataMapping,
                        isConsumerFilterLoadingStoreData: isConsumerFilterLoadingStoreData,
                        removedKpiStoreState: removedKpiStoreState,
                        dataViewKey: dataViewKey,
                        tTestResult: tTestResult,
                    }}
                />
            )}
            <RemoveRestoreDialog
                settings={{
                    openRestoreDialog: openRestoreDialog,
                    removedKpi: removedKpi,
                    showCheckBox: showCheckBox,
                    disableRestore: disableRestore,
                }}
                functionsSettings={{
                    setShowCheckBox: setShowCheckBox,
                    setOpenRestoreDialog: setOpenRestoreDialog,
                }}
                analysisView={{
                    moduleName: moduleName,
                    mappings: tableDataMapping,
                }}
            />
            {!["overall", "claims", "fragranceMetrics", "preference"].includes(moduleName) && (
                <SkewTabs
                    tableData={{
                        productsSecondaryDataState: productsSecondaryData,
                    }}
                    settings={{
                        activeTab: activeTab,
                        setActiveTab: setActiveTab,
                    }}
                    analysisView={{
                        moduleName: moduleName,
                        mappings: mappings,
                        analysisTypeValue: analysisTypeValue,
                        boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                    }}
                />
            )}
            <AnalysisTableBottomLabel
                actionStandard={{
                    actionStandardKpis: actionStandardKpis,
                    actionStandardData: actionStandardData,
                    showActionStandardsInfo: true,
                }}
                tTest={{
                    tTestLegends: tTestLegends,
                    isFisher: isFisher,
                    tTestResult: tTestResult,
                    testLabel: testLabel,
                }}
                analysisView={{
                    moduleName: moduleName,
                }}
            />
            {productDetailsDialogueOpen && (
                <ProductInfo
                    settings={{
                        productDetailsDialogueOpen: productDetailsDialogueOpen,
                        setProductDetailsDialogueOpen: setProductDetailsDialogueOpen,
                    }}
                    info={selectedProductInfo}
                    productsSecondaryData={productsSecondaryData}
                />
            )}
        </div>
    );
}

AnalysisTable.propTypes = {
    productsSecondaryData: PropTypes.array,
    tTest: PropTypes.object,
    handleFilterSubmit: PropTypes.func,
    analysisLevelData: PropTypes.object,
    settings: PropTypes.object,
};
export default AnalysisTable;
